import { useCallback, useState } from 'react';

/** What a search-backed select knows about one option at the moment it is chosen. */
export type LabelEntry = readonly [id: string, label: string];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2a §4c] A SESSION LABEL CACHE FOR A SEARCH-BACKED MULTI-SELECT.
 *
 *  The problem, in one sentence: a chip the operator picked THIS SESSION is almost never in
 *  the CURRENT search response, and rc-select ends `label ?? item.value` — so once the results
 *  rotate, the chip repaints as a raw ObjectId or as `No longer available` (divergence #84).
 *  Remembering the label at the moment of selection is what keeps it readable.
 *
 *  ⚠️ IT REMEMBERS ON **SELECTION**, NOT ON EVERY SEARCH RESPONSE, AND BOTH OTHER SHAPES WERE
 *  TRIED AND REFUSED BY A LINT RULE THAT WAS RIGHT BOTH TIMES.
 *
 *    · a `useRef<Map>` written in an effect and READ IN A `useMemo` — `react-hooks/refs`
 *      refuses it: a ref read during render cannot make the component update, so the first
 *      render after a rotation could legitimately paint the stale label.
 *    · `useState` merged inside a `useEffect` — `react-hooks` refuses a synchronous `setState`
 *      in an effect as a cascading render, and it is: the entries array is rebuilt every
 *      render, so the effect would run every render.
 *
 *  Selection is an EVENT, `setState` in an event handler is ordinary React, and it is also the
 *  more precise question — the only ids whose labels can ever be needed are the ids in `value`.
 *
 *  ⚠️ AND IT RETURNS THE SAME MAP WHEN NOTHING CHANGED, so a caller may pass it to a `useMemo`
 *  dependency list without re-running the memo on every selection of an already-known id.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const useLabelCache = (): {
  labels: ReadonlyMap<string, string>;
  remember: (entries: readonly LabelEntry[]) => void;
} => {
  const [labels, setLabels] = useState<ReadonlyMap<string, string>>(() => new Map());

  const remember = useCallback((entries: readonly LabelEntry[]): void => {
    if (entries.length === 0) return;
    setLabels((current) => {
      const next = new Map(current);
      let changed = false;
      for (const [id, label] of entries) {
        if (label.length > 0 && next.get(id) !== label) {
          next.set(id, label);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, []);

  return { labels, remember };
};

/** rc-select hands `onChange` one option or an array of them, and either may be `undefined`. */
export const labelEntriesOf = (option: unknown): LabelEntry[] => {
  const rows = Array.isArray(option) ? option : [option];
  return rows.flatMap((row): LabelEntry[] => {
    if (typeof row !== 'object' || row === null) return [];
    const { value, label } = row as { value?: unknown; label?: unknown };
    return typeof value === 'string' && typeof label === 'string' ? [[value, label] as const] : [];
  });
};
